import React from 'react';
import { Tooltip } from '@/Components/ui/Tooltip';

interface FormTextareaProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  placeholder?: string;
  required?: boolean;
  error?: string;
  rows?: number;
  disabled?: boolean;
  dataTestId?: string;
  className?: string;
  maxLength?: number;
  minLength?: number;
  size?: 'default' | 'sm';
  tooltip?: string;
}

/**
 * Composant Textarea standardisé pour les formulaires
 * Floating label animé
 */
export const FormTextarea: React.FC<FormTextareaProps> = ({
  value,
  onChange,
  label,
  placeholder,
  required = false,
  error,
  rows = 4,
  disabled = false,
  dataTestId,
  className = '',
  maxLength,
  minLength,
  size = 'default',
  tooltip,
}) => {
  const hasValue = !!value;
  const sm = size === 'sm';
  const textClass = sm ? 'tw-text-sm' : 'tw-text-base';
  const paddingClass = label ? (sm ? 'tw-pt-4 tw-pb-1' : 'tw-pt-5 tw-pb-1.5') : (sm ? 'tw-py-2' : 'tw-py-2.5');
  const labelFloated = sm ? 'tw-top-1 tw-text-[10px]' : 'tw-top-1.5 tw-text-[11px]';
  const labelUnfloated = sm
    ? 'tw-top-[9px] tw-text-sm tw-text-gray-400 peer-focus:tw-top-1 peer-focus:tw-text-[10px]'
    : 'tw-top-[13px] tw-text-base tw-text-gray-400 peer-focus:tw-top-1.5 peer-focus:tw-text-[11px]';

  return (
    <div className={className}>
      <div className="tw-relative">
        <textarea
          data-testid={dataTestId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          placeholder={label || placeholder || ' '}
          rows={rows}
          disabled={disabled}
          maxLength={maxLength}
          minLength={minLength}
          className={`tw-w-full tw-px-3 ${paddingClass} ${textClass} tw-border tw-rounded-xl tw-transition-all tw-peer tw-placeholder-transparent
            ${error ? 'tw-border-red-500' : 'tw-border-gray-200'}
            focus:tw-outline-none focus:tw-ring-2
            ${error ? 'focus:tw-ring-red-500' : 'focus:tw-ring-affinicar-primary/20 focus:tw-border-affinicar-primary'}
            disabled:tw-bg-gray-100 disabled:tw-cursor-not-allowed disabled:tw-text-gray-500
            tw-resize-y`}
        />

        {label && (
          <label
            className={`tw-absolute tw-left-3 tw-pointer-events-none tw-transition-all tw-duration-200 tw-origin-left
              ${hasValue
                ? labelFloated
                : labelUnfloated
              }
              ${error
                ? (hasValue ? 'tw-text-red-500' : 'peer-focus:tw-text-red-500')
                : (hasValue ? 'tw-text-affinicar-primary' : 'peer-focus:tw-text-affinicar-primary')
              }
            `}
          >
            {label}
            {!required && <span className="tw-text-gray-400 tw-font-normal tw-ml-1 tw-text-[10px]">(Optionnel)</span>}
            {tooltip && <Tooltip content={tooltip} />}
          </label>
        )}
      </div>

      {error && (
        <p className="tw-mt-1 tw-text-sm tw-text-red-600">{error}</p>
      )}
    </div>
  );
};
